import React, { FC, memo, useMemo } from 'react'
import { LayoutComponentProps, LayoutProps } from '@sport1/types/web'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { useRouter } from 'next/router'
import { useNavigation } from '@/utils/navigation/Standard'
import renderSideColumnComponent from '@/helpers/renderSideColumnComponent'
import { getOutbrainAd } from '@/pages/api/article/sidecolumn/ads'
import { getNewstickerLayout } from '@/pages/api/article/sidecolumn/newsticker'
import { getTopVideoListTeaserLayout } from '@/pages/api/article/sidecolumn/top-video-list-teaser'
import { useTracking } from '@/utils/tracking/TrackingProvider'
import { useAdPlacementContext } from '@/context/AdPlacementContext/AdPlacementProvider'
import { isMobileView } from '@/helpers/pageHelper'
import { createExtraAdSlot } from '@/utils/ads/utils'
import useIsOnlineCasino from '@/hooks/useIsOnlineCasino'

type Props = {
    isAdsActive?: boolean // to disable ads, need to pass 'false'
}

type FulfilledLayout = PromiseFulfilledResult<LayoutProps | undefined>

const SideColumn: FC<Props> = ({ isAdsActive = true }) => {
    const { navigation } = useNavigation()
    const { asPath } = useRouter()
    const [components, setLayoutComponents] = React.useState<LayoutComponentProps[]>([])
    const { trackingData } = useTracking()
    const { enrichAdsWithId, placements } = useAdPlacementContext()
    const isOnlineCasino = useIsOnlineCasino()

    const adLevels = useMemo(
        () =>
            Object.keys(trackingData)
                .filter(key => key.startsWith('page_adLevel'))
                .map(key => trackingData[key]) as string[],
        [trackingData]
    )

    React.useEffect(() => {
        if (components.length === 0 && !isMobileView()) {
            const adOutbrain = isAdsActive ? [getOutbrainAd()] : []
            const adSlot = isAdsActive
                ? [
                      createExtraAdSlot({
                          placement: 'rectangle',
                          placementId: 'rectangle10',
                          placements,
                      }),
                  ]
                : []
            const newstickerPromise = getNewstickerLayout(navigation?.tag, asPath)
            const topVideosPromise = isOnlineCasino
                ? Promise.resolve({ status: 'fulfilled', value: { components: [] } })
                : getTopVideoListTeaserLayout()
            Promise.allSettled([newstickerPromise, topVideosPromise]).then(
                ([newsticker, topVideos]) => {
                    const nextComponents = [
                        ...adSlot,
                        ...((newsticker as FulfilledLayout)?.value?.components || []),
                        ...((topVideos as FulfilledLayout)?.value?.components || []),
                        ...adOutbrain,
                    ] as LayoutComponentProps[]
                    enrichAdsWithId(nextComponents)
                    setLayoutComponents(nextComponents)
                }
            )
        }
    }, [
        adLevels,
        components,
        enrichAdsWithId,
        isAdsActive,
        navigation?.tag,
        placements,
        isOnlineCasino,
        asPath,
    ])

    if (isMobileView()) return null

    return (
        <NonFlexingContainer testID="side-column">
            {components.map((component: LayoutComponentProps) => {
                return renderSideColumnComponent({ component })
            })}
        </NonFlexingContainer>
    )
}

export default memo(SideColumn)
